import { useState, useEffect, useRef } from "react";
import api, { fileBlobUrl } from "@/lib/api";
import { renderAnnotatedDesignPdf } from "@/lib/annotatedDesignPdf";
import PdfPinViewer from "@/components/PdfPinViewer";
import { MapPin, X, ChatCircle, Download, PencilSimple, Trash, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Image => pin annotation. PDF => multi-page pdf.js render with per-page pins.
// Toolbar sits ABOVE the artwork in its own row so it never covers the design.
export default function DesignFileViewer({ jobId, job, design, file, onCommentAdded, readOnly = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [url, setUrl] = useState(null);
  const [isImage, setIsImage] = useState(true);
  const [comments, setComments] = useState([]);
  const [placingPin, setPlacingPin] = useState(false);
  const [pinDraft, setPinDraft] = useState(null);
  const [text, setText] = useState("");
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const imgRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const meta = await api.get(`/files/${file.file_id}`);
        const ct = meta.data.content_type || "";
        setIsImage(ct.startsWith("image/"));
        const u = await fileBlobUrl(file.file_id);
        setUrl(u);
      } catch (e) { /* ignore file load errors */ }
    })();
  }, [file.file_id]);

  useEffect(() => { loadComments(); }, [design.id, file.id]);

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/comments`, { params: { design_id: design.id } });
      setComments(data.filter((c) => c.file_id === file.file_id || (!c.file_id)));
    } catch { /* ignore */ }
  };

  const handleImageClick = (e) => {
    if (!placingPin || readOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPinDraft({ x, y, page: null });
  };

  const handlePdfPageClick = (pageNumber, x, y) => {
    if (readOnly) return;
    setPinDraft({ page: pageNumber, x, y });
  };

  const submitComment = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/comments`, {
        design_id: design.id, file_id: file.file_id, text,
        pin_x: pinDraft?.x ?? null, pin_y: pinDraft?.y ?? null,
        pin_page: pinDraft?.page ?? null,
      });
      setText(""); setPinDraft(null); setPlacingPin(false);
      await loadComments();
      onCommentAdded?.();
    } catch { toast.error("Failed to add comment"); }
  };

  const startEditComment = (c) => {
    setEditingCommentId(c.id);
    setEditingText(c.text);
    setActiveCommentId(c.id);
  };
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingText("");
  };
  const saveEditComment = async (c) => {
    if (!editingText.trim()) return toast.error("Comment can't be empty");
    try {
      await api.patch(`/admin/jobs/${jobId}/comments/${c.id}`, { text: editingText });
      cancelEditComment();
      await loadComments();
      toast.success("Comment updated");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to update comment");
    }
  };
  const deleteComment = async (c) => {
    if (!window.confirm(`Delete this comment by ${c.author_name}?\n\n"${c.text.slice(0, 120)}${c.text.length > 120 ? "…" : ""}"`)) return;
    try {
      await api.delete(`/admin/jobs/${jobId}/comments/${c.id}`);
      await loadComments();
      toast.success("Comment deleted");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to delete comment");
    }
  };

  const [downloading, setDownloading] = useState(false);
  const downloadAnnotated = async (e) => {
    // Only for images with at least one comment — otherwise let the raw <a download> fire.
    if (!isImage || comments.length === 0) return;
    e.preventDefault();
    setDownloading(true);
    try {
      const blob = await renderAnnotatedDesignPdf({
        imageUrl: url,
        comments,
        job,
        iteration: design,
        file,
      });
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `${file.design_number || "design"}-review.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      toast.success(`Exported PDF with ${comments.length} comment${comments.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Couldn't render review PDF — downloading raw file instead");
      // Fallback to raw download so the user still gets *something*
      const a = document.createElement("a");
      a.href = url; a.download = file.design_number || "design";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } finally { setDownloading(false); }
  };

  // Pin numbering: only comments with a pin (pin_x != null) get a number.
  // Both the image markers and the sidebar badges use the SAME ordinal so
  // pin #3 on the image matches "pin 3" in the sidebar even when non-pin
  // comments are interleaved.
  const pinComments = comments.filter((c) => c.pin_x != null);
  const pinNumberById = new Map(pinComments.map((c, i) => [c.id, i + 1]));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4" data-testid={`design-file-${file.id}`}>
      <div>
        {/* Toolbar — outside the image, above it */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2" data-testid={`design-toolbar-${file.id}`}>
          <div className="chip chip-navy" data-testid={`design-label-${file.id}`}>{file.design_number}</div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <button
                onClick={() => { setPlacingPin(!placingPin); setPinDraft(null); }}
                className={`text-xs px-3 py-1.5 border transition-colors font-semibold ${placingPin ? 'bg-[var(--gc-orange)] text-white border-[var(--gc-orange)]' : 'bg-white border-[var(--gc-navy)] text-[var(--gc-navy)] hover:bg-[var(--gc-bg-alt)]'}`}
                data-testid={`pin-toggle-${file.id}`}
              >
                <MapPin size={13} className="inline mr-1" weight="fill"/> {placingPin ? "Cancel Pin" : "Drop Pin"}
              </button>
            )}
            {url && (
              <a
                href={url}
                download={file.design_number || "design"}
                onClick={downloadAnnotated}
                className="text-xs px-3 py-1.5 border border-[var(--gc-navy)] text-[var(--gc-navy)] hover:bg-[var(--gc-bg-alt)] font-semibold inline-flex items-center gap-1"
                title={isImage && comments.length > 0 ? `Export PDF with ${comments.length} pin${comments.length === 1 ? "" : "s"} + comments` : "Download raw file"}
                data-testid={`download-${file.id}`}
              >
                <Download size={13} className="inline mr-1"/>
                {downloading
                  ? "Rendering…"
                  : (isImage && comments.length > 0 ? `Export PDF (+${comments.length} comment${comments.length === 1 ? "" : "s"})` : "Download")}
              </a>
            )}
          </div>
        </div>

        {/* Image area — no clipping, controls are OUTSIDE and above */}
        <div className="border border-[var(--gc-border)] bg-white flex items-center justify-center overflow-auto">
          {!url ? (
            <div className="text-[var(--gc-text-muted)] py-16 font-mono-tech text-sm">LOADING…</div>
          ) : isImage ? (
            <div className="relative w-full" onClick={handleImageClick} style={{ cursor: placingPin ? 'crosshair' : 'default' }}>
              <img ref={imgRef} src={url} alt={file.design_number} className="w-full block" data-testid={`design-image-${file.id}`} />
              {pinComments.filter((c) => !c.pin_page).map((c) => (
                <button key={c.id} type="button"
                  onClick={(e) => { e.stopPropagation(); setActiveCommentId(c.id); }}
                  style={{ left: `${c.pin_x * 100}%`, top: `${c.pin_y * 100}%` }}
                  className="absolute -translate-x-1/2 -translate-y-full"
                  data-testid={`pin-marker-${c.id}`}
                >
                  <span className="bg-[var(--gc-orange)] text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white">
                    {pinNumberById.get(c.id)}
                  </span>
                </button>
              ))}
              {pinDraft && !pinDraft.page && (
                <div style={{ left: `${pinDraft.x * 100}%`, top: `${pinDraft.y * 100}%` }} className="absolute -translate-x-1/2 -translate-y-full">
                  <span className="bg-[var(--gc-navy)] text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ring-4 ring-[var(--gc-orange)]/40 border-2 border-white">
                    ?
                  </span>
                </div>
              )}
            </div>
          ) : (
            <PdfPinViewer
              url={url}
              pinComments={pinComments}
              pinNumberById={pinNumberById}
              pinDraft={pinDraft}
              placingPin={placingPin && !readOnly}
              onPageClick={handlePdfPageClick}
              onPinClick={setActiveCommentId}
              activeCommentId={activeCommentId}
            />
          )}
        </div>
      </div>

      <div className="border border-[var(--gc-border)] bg-white flex flex-col max-h-[75vh]">
        <div className="px-4 py-2 border-b border-[var(--gc-border)] bg-[var(--gc-bg-alt)] flex items-center gap-2">
          <ChatCircle size={16} className="text-[var(--gc-orange)]" weight="fill" />
          <div className="tech-label">Comments · {comments.length}</div>
        </div>
        <div className="flex-1 overflow-auto p-3 space-y-2">
          {comments.length === 0 && <div className="text-[var(--gc-text-muted)] text-sm">No comments yet. {!readOnly && "Drop a pin to comment on a specific area."}</div>}
          {comments.map((c) => {
            const isEditing = editingCommentId === c.id;
            const pinNum = pinNumberById.get(c.id);
            return (
              <div key={c.id}
                className={`p-3 border transition-colors ${activeCommentId === c.id ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-border)] bg-white hover:bg-[var(--gc-bg-alt)]'} ${isEditing ? '' : 'cursor-pointer'}`}
                onClick={() => !isEditing && setActiveCommentId(c.id)}
                data-testid={`comment-${c.id}`}
              >
                <div className="flex items-center justify-between mb-1 gap-2">
                  <div className="text-xs font-semibold flex items-center gap-2 min-w-0">
                    {pinNum != null && (
                      <span className="bg-[var(--gc-orange)] text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] shrink-0" data-testid={`comment-pin-num-${c.id}`}>
                        {pinNum}
                      </span>
                    )}
                    {c.pin_page != null && (
                      <span className="tech-label shrink-0" data-testid={`comment-pin-page-${c.id}`}>p.{c.pin_page}</span>
                    )}
                    <span className="truncate">{c.author_name}</span> <span className="tech-label ml-1 shrink-0">{c.author_role.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <div className="tech-label">{new Date(c.created_at).toLocaleDateString()}</div>
                    {isAdmin && !isEditing && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startEditComment(c); }}
                          className="ml-1 p-1 text-[var(--gc-text-muted)] hover:text-[var(--gc-orange)]"
                          title="Edit comment"
                          data-testid={`edit-comment-${c.id}`}
                        >
                          <PencilSimple size={13} weight="bold"/>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); deleteComment(c); }}
                          className="p-1 text-[var(--gc-text-muted)] hover:text-[var(--gc-danger)]"
                          title="Delete comment"
                          data-testid={`delete-comment-${c.id}`}
                        >
                          <Trash size={13} weight="bold"/>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="gc-input min-h-[64px] text-sm"
                      data-testid={`edit-comment-input-${c.id}`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEditComment(c)}
                        disabled={!editingText.trim()}
                        className="btn-primary flex-1 inline-flex items-center justify-center gap-1 text-xs px-2 py-1.5 disabled:opacity-50"
                        data-testid={`save-comment-${c.id}`}
                      >
                        <Check size={12} weight="bold"/> Save
                      </button>
                      <button
                        onClick={cancelEditComment}
                        className="btn-secondary flex-1 inline-flex items-center justify-center gap-1 text-xs px-2 py-1.5"
                        data-testid={`cancel-edit-comment-${c.id}`}
                      >
                        <X size={12} weight="bold"/> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-[var(--gc-navy)] whitespace-pre-wrap">{c.text}</div>
                    {c.edited_at && (
                      <div className="tech-label mt-1 text-[10px] italic" data-testid={`comment-edited-${c.id}`}>
                        edited{c.edited_by_name ? ` by ${c.edited_by_name}` : ""} · {new Date(c.edited_at).toLocaleString()}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <div className="p-3 border-t border-[var(--gc-border)] space-y-2 bg-[var(--gc-bg-alt)]">
            {pinDraft && <div className="tech-label text-[var(--gc-orange)]">Pin placed. Add a note ↓</div>}
            <textarea
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder={pinDraft ? "Note for this pin…" : "Comment on this design…"}
              className="gc-input min-h-[64px]"
              data-testid={`comment-input-${file.id}`}
            />
            <button onClick={submitComment} disabled={!text.trim()} className="btn-primary w-full py-2 text-sm disabled:opacity-50" data-testid={`comment-submit-${file.id}`}>
              Post Comment
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
